// AppShell — protected-subtree layout route element (legacy
// src/App.jsx equivalent). CSS-Grid composition of Sidebar + Topbar + main.
// Below the 900px `useNarrow()` breakpoint, the Sidebar is
// hidden and a hamburger in Topbar opens the <MobileNav /> drawer.
//
// Root-grid bg = --bg-0 (warm off-white) matches legacy src/shell/App.jsx:69.
// <main /> inherits it, giving tables / forms / cards (which explicitly use
// --bg-1 white) a visible backdrop contrast — without this, everything
// collapses onto one flat white canvas.
import { useState } from 'react';
import { Outlet } from 'react-router';
import { Sidebar } from './Sidebar.js';
import { Topbar } from './Topbar.js';
import { MobileNav } from './MobileNav.js';
import { useNarrow, SkipLink } from '../ui/index.js';
import { useSidebarStore } from '../stores/sidebar.js';
import { RealtimeBootstrap } from '../realtime/RealtimeBootstrap.js';

export function AppShell() {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const narrow = useNarrow();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <RealtimeBootstrap />
      <div
        style={{
          display: 'grid',
          // WHY: `minmax(0, 1fr)` (not `1fr`) so the narrow grid column
          // never grows past the viewport when a descendant has a large
          // intrinsic min-content. Pairs with `minWidth: 0` on `<main>`.
          gridTemplateColumns: narrow
            ? 'minmax(0, 1fr)'
            : collapsed
              ? 'var(--sidebar-w-collapsed) 1fr'
              : 'var(--sidebar-w) 1fr',
          gridTemplateRows: 'var(--topbar-h) 1fr',
          gridTemplateAreas: narrow ? `"topbar" "main"` : `"sidebar topbar" "sidebar main"`,
          height: '100vh',
          background: 'var(--bg-0)',
        }}
      >
        <SkipLink targetId="main-content" />
        {!narrow && (
          <div style={{ gridArea: 'sidebar' }}>
            <Sidebar />
          </div>
        )}
        <div style={{ gridArea: 'topbar' }}>
          <Topbar {...(narrow ? { onMobileMenu: () => setMobileOpen(true) } : {})} />
        </div>
        {/* WHY: skip-link target. tabIndex=-1 lets the SkipLink move focus here (not just scroll); outline:none keeps the landmark from flashing the global focus ring when focused programmatically (WebAIM skip-target pattern). */}
        <main
          id="main-content"
          tabIndex={-1}
          style={{
            gridArea: 'main',
            overflow: 'auto',
            padding: 'var(--s-5)',
            minWidth: narrow ? 0 : undefined,
            outline: 'none',
          }}
        >
          <Outlet />
        </main>
      </div>
      {narrow && <MobileNav open={mobileOpen} onClose={() => setMobileOpen(false)} />}
    </>
  );
}
